import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaTrash,
  FaPaperPlane,
  FaCheck,
  FaPhone,
  FaReply,
  FaPaperclip,
  FaDownload,
  FaTrashRestore,
  FaTimes,
  FaCheckSquare,
  FaSquare,
  FaInbox,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { authFetch } from './authHelper';

const FOLDER_LABELS = {
  inbox: 'Reçus',
  sent: 'Envoyés',
  trash: 'Supprimés',
};

const AdminMessages = ({ folder = 'inbox', onUnreadChange }) => {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messageDetail, setMessageDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchMessages = useCallback(() => {
    setLoading(true);
    authFetch(`/api/admin/messages.php?folder=${folder}`)
      .then((res) => res.json())
      .then((data) => {
        if (folder === 'inbox') {
          setMessages(Array.isArray(data.messages) ? data.messages : []);
          if (onUnreadChange && data.unread_count !== undefined) {
            onUnreadChange(data.unread_count);
          }
        } else {
          setMessages(Array.isArray(data) ? data : []);
        }
        setLoading(false);
      })
      .catch(() => { setMessages([]); setLoading(false); });
  }, [folder, onUnreadChange]);

  const fetchDetail = useCallback((messageId) => {
    authFetch(`/api/admin/messages.php?id=${messageId}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) setMessageDetail(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelected(null);
    setMessageDetail(null);
    setCheckedIds(new Set());
    fetchMessages();
  }, [folder, fetchMessages]);

  const markRead = async (id) => {
    await authFetch(`/api/admin/messages.php?id=${id}`, { method: 'PUT' });
    fetchMessages();
  };

  // Bulk trash (inbox -> trash)
  const handleBulkTrash = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} message(s) ?`)) return;

    await authFetch(`/api/admin/messages.php?action=trash&ids=${ids.join(',')}`, { method: 'PUT' });
    setCheckedIds(new Set());
    if (selected && ids.includes(selected.id)) { setSelected(null); setMessageDetail(null); }
    fetchMessages();
  };

  // Bulk restore (trash -> inbox)
  const handleBulkRestore = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;

    await authFetch(`/api/admin/messages.php?action=restore&ids=${ids.join(',')}`, { method: 'PUT' });
    setCheckedIds(new Set());
    if (selected && ids.includes(selected.id)) { setSelected(null); setMessageDetail(null); }
    fetchMessages();
  };

  // Permanent delete (from trash)
  const handleBulkDelete = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} message(s) ? Cette action est irréversible.`)) return;

    await authFetch(`/api/admin/messages.php?ids=${ids.join(',')}`, { method: 'DELETE' });
    setCheckedIds(new Set());
    if (selected && ids.includes(selected.id)) { setSelected(null); setMessageDetail(null); }
    fetchMessages();
  };

  // Single message trash/delete
  const handleTrashOrDelete = async (id) => {
    if (folder === 'trash') {
      if (!window.confirm('Supprimer définitivement ce message ?')) return;
      await authFetch(`/api/admin/messages.php?ids=${id}`, { method: 'DELETE' });
    } else {
      if (!window.confirm('Mettre ce message à la corbeille ?')) return;
      await authFetch(`/api/admin/messages.php?action=trash&ids=${id}`, { method: 'PUT' });
    }
    if (selected && selected.id === id) { setSelected(null); setMessageDetail(null); }
    fetchMessages();
  };

  // Single restore
  const handleRestore = async (id) => {
    await authFetch(`/api/admin/messages.php?action=restore&ids=${id}`, { method: 'PUT' });
    if (selected && selected.id === id) { setSelected(null); setMessageDetail(null); }
    fetchMessages();
  };

  const openMessage = (msg) => {
    setSelected(msg);
    setReplyText('');
    setReplyStatus(null);
    setAttachments([]);
    // For inbox/trash, fetch full detail with replies/attachments
    if (folder !== 'sent') {
      fetchDetail(msg.id);
      if (folder === 'inbox' && !parseInt(msg.is_read)) markRead(msg.id);
    } else {
      // For sent messages, fetch the original message detail
      fetchDetail(msg.message_id);
    }
  };

  // Checkbox toggle
  const toggleCheck = (id, e) => {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === messages.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(messages.map(m => m.id)));
    }
  };

  // File attachments
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  // Reply with attachments
  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    const targetMsg = messageDetail || selected;
    if (!targetMsg) return;

    setSending(true);
    setReplyStatus(null);

    try {
      const formData = new FormData();
      formData.append('to', targetMsg.email);
      formData.append('subject', `Re: ${targetMsg.subject || 'Votre demande'} - AGKG Créations`);
      formData.append('body', replyText);
      formData.append('message_id', targetMsg.id);

      attachments.forEach((file) => {
        formData.append('attachments[]', file);
      });

      const res = await authFetch('/api/admin/reply.php', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setReplyStatus('success');
        setReplyText('');
        setAttachments([]);
        fetchDetail(targetMsg.id);
        fetchMessages();
      } else {
        setReplyStatus('error');
      }
    } catch {
      setReplyStatus('error');
    }

    setSending(false);
  };

  if (loading) return <div className="admin-loading">Chargement...</div>;

  const folderLabel = FOLDER_LABELS[folder] || 'Messages';
  const allChecked = messages.length > 0 && checkedIds.size === messages.length;

  return (
    <div>
      <h2 className="admin-page-title">{folderLabel} ({messages.length})</h2>

      {/* Bulk actions bar */}
      {messages.length > 0 && (
        <div className="admin-bulk-bar">
          <button className="admin-bulk-check" onClick={toggleAll} title={allChecked ? 'Tout désélectionner' : 'Tout sélectionner'}>
            {allChecked ? <FaCheckSquare /> : <FaSquare />}
            <span>{allChecked ? 'Tout désélectionner' : 'Tout sélectionner'}</span>
          </button>
          {checkedIds.size > 0 && (
            <div className="admin-bulk-actions">
              <span className="admin-bulk-count">{checkedIds.size} sélectionné(s)</span>
              {folder === 'trash' ? (
                <>
                  <button className="btn btn-sm btn-restore" onClick={handleBulkRestore}>
                    <FaTrashRestore /> Restaurer
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
                    <FaTrash /> Supprimer définitivement
                  </button>
                </>
              ) : (
                <button className="btn btn-sm btn-danger" onClick={handleBulkTrash}>
                  <FaTrash /> Supprimer
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="admin-messages-layout">
        <div className="admin-messages-list">
          {messages.length === 0 ? (
            <div className="admin-empty-folder">
              <FaInbox className="admin-empty-icon" />
              <p>Aucun message dans {folderLabel.toLowerCase()}</p>
            </div>
          ) : folder === 'sent' ? (
            // Sent view: show replies
            messages.map((reply) => (
              <div
                key={reply.id}
                className={`admin-message-item admin-message-read ${selected?.id === reply.id ? 'selected' : ''}`}
                onClick={() => openMessage(reply)}
              >
                <div className="admin-message-item-inner">
                  <div
                    className="admin-message-checkbox"
                    onClick={(e) => toggleCheck(reply.id, e)}
                  >
                    {checkedIds.has(reply.id) ? <FaCheckSquare /> : <FaSquare />}
                  </div>
                  <div className="admin-message-content">
                    <div className="admin-message-header">
                      <strong>A : {reply.recipient_name || reply.sent_to}</strong>
                      <span className="admin-message-date">
                        {new Date(reply.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <div className="admin-message-subject">
                      Re: {reply.original_subject || 'Sans sujet'}
                    </div>
                    <div className="admin-message-preview">{(reply.reply_body || '').substring(0, 80)}...</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Inbox / Trash view
            messages.map((msg) => {
              const isUnread = !parseInt(msg.is_read);
              return (
                <div
                  key={msg.id}
                  className={`admin-message-item ${isUnread ? 'unread' : 'admin-message-read'} ${selected?.id === msg.id ? 'selected' : ''}`}
                  onClick={() => openMessage(msg)}
                >
                  <div className="admin-message-item-inner">
                    <div
                      className="admin-message-checkbox"
                      onClick={(e) => toggleCheck(msg.id, e)}
                    >
                      {checkedIds.has(msg.id) ? <FaCheckSquare /> : <FaSquare />}
                    </div>
                    <div className="admin-message-content">
                      <div className="admin-message-header">
                        <strong className={isUnread ? 'admin-text-unread' : ''}>{msg.name}</strong>
                        <span className="admin-message-date">
                          {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <div className={`admin-message-subject ${isUnread ? 'admin-text-unread' : ''}`}>
                        {msg.subject || 'Sans sujet'}
                        {msg.reply_count > 0 && (
                          <span className="admin-reply-badge">
                            <FaReply /> {msg.reply_count}
                          </span>
                        )}
                        {isUnread && <span className="admin-unread-dot" />}
                      </div>
                      <div className={`admin-message-preview ${isUnread ? 'admin-preview-unread' : ''}`}>
                        {(msg.message || '').substring(0, 80)}...
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        {selected && folder === 'sent' ? (
          // Sent detail: show the sent reply
          <div className="admin-message-detail">
            <div className="admin-message-detail-header">
              <h3>Re: {selected.original_subject || 'Sans sujet'}</h3>
            </div>
            <div className="admin-message-meta">
              <p><strong>A :</strong> {selected.recipient_name} ({selected.sent_to})</p>
              <p><strong>Date :</strong> {new Date(selected.created_at).toLocaleString('fr-FR')}</p>
            </div>
            <div className="admin-message-body">{selected.reply_body}</div>

            {/* Show full conversation if detail loaded */}
            {messageDetail && (
              <div className="admin-replies-history">
                <h4><FaReply /> Conversation complète</h4>
                <div className="admin-reply-item admin-reply-original">
                  <div className="admin-reply-meta">
                    <span>De : {messageDetail.name} ({messageDetail.email})</span>
                    <span>{new Date(messageDetail.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="admin-reply-body">{messageDetail.message}</div>
                </div>
                {(messageDetail.replies || []).map((reply) => (
                  <div key={reply.id} className="admin-reply-item">
                    <div className="admin-reply-meta">
                      <span>Envoyé à {reply.sent_to}</span>
                      <span>{new Date(reply.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="admin-reply-body">{reply.reply_body}</div>
                    {reply.attachments && reply.attachments.length > 0 && (
                      <div className="admin-attachment-list">
                        {reply.attachments.map((att) => (
                          <a
                            key={att.id}
                            href={`/api/admin/attachments.php?id=${att.id}`}
                            className="admin-attachment-item"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaPaperclip /> {att.original_name} ({formatFileSize(att.file_size)})
                            <FaDownload />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : selected && (folder === 'inbox' || folder === 'trash') ? (
          // Inbox / Trash detail
          <div className="admin-message-detail">
            <div className="admin-message-detail-header">
              <h3>{selected.subject || 'Sans sujet'}</h3>
              <div className="admin-detail-actions">
                {folder === 'trash' && (
                  <button className="admin-action-btn restore" onClick={() => handleRestore(selected.id)} title="Restaurer">
                    <FaTrashRestore />
                  </button>
                )}
                <button className="admin-action-btn danger" onClick={() => handleTrashOrDelete(selected.id)} title="Supprimer">
                  <FaTrash />
                </button>
              </div>
            </div>
            <div className="admin-message-meta">
              <p><strong>De :</strong> {selected.name} ({selected.email})</p>
              {selected.phone && <p><strong>Tél :</strong> {selected.phone}</p>}
              <p><strong>Date :</strong> {new Date(selected.created_at).toLocaleString('fr-FR')}</p>
            </div>
            <div className="admin-message-body">
              {selected.message}
            </div>

            {/* Attachments on original message */}
            {messageDetail?.attachments && messageDetail.attachments.length > 0 && (
              <div className="admin-attachment-list">
                <h4><FaPaperclip /> Pièces jointes</h4>
                {messageDetail.attachments.map((att) => (
                  <a
                    key={att.id}
                    href={`/api/admin/attachments.php?id=${att.id}`}
                    className="admin-attachment-item"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaPaperclip /> {att.original_name} ({formatFileSize(att.file_size)})
                    <FaDownload />
                  </a>
                ))}
              </div>
            )}

            {selected.phone && (
              <div style={{ marginBottom: '20px' }}>
                <a href={`tel:${selected.phone}`} className="btn btn-secondary btn-sm">
                  <FaPhone /> Appeler
                </a>
              </div>
            )}

            {/* Reply history */}
            {messageDetail?.replies && messageDetail.replies.length > 0 && (
              <div className="admin-replies-history">
                <h4><FaReply /> Historique des réponses ({messageDetail.replies.length})</h4>
                {messageDetail.replies.map((reply) => (
                  <div key={reply.id} className="admin-reply-item">
                    <div className="admin-reply-meta">
                      <span>Envoyé à {reply.sent_to}</span>
                      <span>{new Date(reply.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="admin-reply-body">{reply.reply_body}</div>
                    {reply.attachments && reply.attachments.length > 0 && (
                      <div className="admin-attachment-list">
                        {reply.attachments.map((att) => (
                          <a
                            key={att.id}
                            href={`/api/admin/attachments.php?id=${att.id}`}
                            className="admin-attachment-item"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaPaperclip /> {att.original_name} ({formatFileSize(att.file_size)})
                            <FaDownload />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Reply form (only in inbox) */}
            {folder === 'inbox' && (
              <form className="admin-reply-form" onSubmit={handleReply}>
                <h4>Répondre à {selected.name}</h4>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows="5"
                  placeholder="Écrivez votre réponse..."
                  required
                />

                {/* Drop zone & file picker */}
                <div
                  ref={dropZoneRef}
                  className={`admin-dropzone ${dragOver ? 'active' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FaPaperclip />
                  <span>Glisser des fichiers ici ou cliquer pour ajouter</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Attached files preview */}
                {attachments.length > 0 && (
                  <div className="admin-attachments-preview">
                    {attachments.map((file, i) => (
                      <div key={i} className="admin-attachment-preview-item">
                        <FaPaperclip />
                        <span>{file.name}</span>
                        <span className="admin-attachment-size">{formatFileSize(file.size)}</span>
                        <button type="button" onClick={() => removeAttachment(i)} className="admin-attachment-remove">
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="admin-reply-actions">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={sending}>
                    {sending ? 'Envoi...' : <><FaPaperPlane /> Envoyer la réponse</>}
                  </button>
                </div>

                {replyStatus === 'success' && (
                  <div className="form-status form-success">
                    <FaCheck /> Réponse envoyée à {selected.email}
                  </div>
                )}
                {replyStatus === 'error' && (
                  <div className="form-status form-error">
                    <FaExclamationTriangle /> Erreur lors de l'envoi. Réessayez.
                  </div>
                )}
              </form>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminMessages;
